'use client';

import * as React from 'react';
import Image from 'next/image';
import { X } from '@phosphor-icons/react';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'azia-app-banner-dismissed';
const IOS_LINK = 'https://apps.apple.com/ru/app/азиаточка-доставка-еды/id6761508832';
const ANDROID_LINK = 'https://play.google.com/store/apps/details?id=com.steepdesign.aziaapp';
const CSS_VAR = '--app-banner-h';

type Platform = 'ios' | 'android' | 'other';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !('MSStream' in window)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'other';
}

/**
 * Smart App Banner — узкая полоса от края до края наверху страницы.
 * Использует тот же цвет/паттерн, что и FloatingMiniCart (brand-градиент
 * на light, тёмный glass на dark).
 * Кладёт свою высоту в CSS-переменную --app-banner-h, Header читает её
 * и сдвигается вниз. При закрытии (крестик) — переменная сбрасывается.
 */
export function SmartAppBanner() {
  const [mounted, setMounted] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);
  const [platform, setPlatform] = React.useState<Platform>('other');
  const ref = React.useRef<HTMLDivElement | null>(null);
  const { resolvedTheme } = useTheme();
  const isDark = mounted ? resolvedTheme === 'dark' : false;

  React.useEffect(() => {
    setMounted(true);
    setPlatform(detectPlatform());
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') setDismissed(true);
    } catch {
      // ignore
    }
  }, []);

  React.useEffect(() => {
    const html = document.documentElement;
    if (dismissed || !mounted) {
      html.style.setProperty(CSS_VAR, '0px');
      return;
    }
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      html.style.setProperty(CSS_VAR, `${el.offsetHeight}px`);
    };
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => {
      ro.disconnect();
      html.style.setProperty(CSS_VAR, '0px');
    };
  }, [dismissed, mounted]);

  const handleDismiss = React.useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore
    }
  }, []);

  if (!mounted || dismissed) return null;

  const href = platform === 'android' ? ANDROID_LINK : IOS_LINK;
  const platformLabel = platform === 'android' ? 'Google Play' : 'App Store';

  return (
    <div
      ref={ref}
      className="fixed inset-x-0 top-0 z-[51] w-full md:hidden"
    >
      {/* Background — паттерн FloatingMiniCart */}
      <div
        className={cn(
          'absolute inset-0',
          isDark
            ? 'bg-[var(--bg-primary)] border-b border-white/[0.06]'
            : 'bg-[var(--primary)]',
        )}
      />
      {!isDark && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(135deg, var(--bonus-platinum), var(--primary))',
          }}
        />
      )}

      {/* Content */}
      <div className="relative flex items-center gap-3 px-3 py-2.5">
        {/* Brand-tile с логотипом */}
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white/15"
          style={{
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.25)',
          }}
        >
          <Image
            src="/logohead.svg"
            alt="Азиаточка"
            width={28}
            height={28}
            className="object-contain"
            priority
          />
        </span>

        {/* Текст: две строки */}
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="whitespace-nowrap text-[14px] font-semibold text-white">
            Доступно приложение
          </span>
          <span className="mt-0.5 truncate text-[12px] text-white/70">
            Скачайте прямо сейчас
          </span>
        </div>

        {/* CTA */}
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Скачать в ${platformLabel}`}
          className="flex h-9 shrink-0 items-center justify-center whitespace-nowrap rounded-full px-4 text-[13px] font-bold transition-transform active:scale-[0.97]"
          style={{
            backgroundColor: '#ffffff',
            color: 'var(--primary)',
            boxShadow:
              '0 4px 12px -2px rgba(0, 0, 0, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
          }}
        >
          Скачать
        </a>

        {/* Close */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Закрыть"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 active:scale-95"
        >
          <X size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}
